// Simplified Chinese copy for /preview-first-uploads, translated from copy.en.ts. Same shape (checked by
// copy.test.ts). "您" and "画廊" as in the site's zh locale. The second pass is always "原图" (never "完整画质").
// The uploader's own labels are in English, so they're quoted as it shows them.
import type { PreviewFirstCopy } from "./copy.en";

const zh: PreviewFirstCopy = {
  seo: {
    title: "预览图优先上传（所有计划） | Photohawk",
    description:
      "Photohawk 网页上传工具新功能，适用于所有计划：每张照片先上传一张小预览图，您的画廊几分钟内即可上线、销售和分享，原图则在后台继续上传。",
  },
  hero: {
    badge: "新功能",
    eyebrow: "网页上传工具 · 所有计划",
    title: "您的画廊几分钟上线，",
    titleHighlight: "而不是几个小时",
    subtitle:
      "网页上传工具现在会先发送每张照片的小预览图。您的画廊可以凭这些预览图先上线，买家可以查找、购买和分享自己的照片，原图则在后台继续上传。",
    ctaPrimary: "打开上传工具",
    ctaSecondary: "了解运作方式",
    uploaderLink: "查看网页上传工具的全部新功能",
  },
  steps: {
    heading: "运作方式",
    subtitle: "所有计划的网页上传工具都提供预览图优先。添加照片时，它会显示每种方式所需的时间，由您选择“Previews first, originals later”。",
    stepLabel: "第 {n} 步",
    items: [
      {
        id: "add",
        title: "添加照片",
        body: "像平时一样，拖入照片或整个文件夹。浏览器会直接在您的电脑上为每张照片生成一张小预览图。",
      },
      {
        id: "previews",
        title: "预览图先上传",
        body: "预览图很小，所以上传很快，即使现场网络很慢也没问题。",
      },
      {
        id: "live",
        title: "画廊上线",
        body: "预览图上传完成后即可发布；也可以勾选“Publish the gallery as soon as the previews are up”，让画廊自动发布。买家马上就能浏览、搜索、购买和分享。",
      },
      {
        id: "full",
        title: "原图随后跟上",
        body: "原图在后台继续上传，并更新到同一批照片中。下载和冲印始终使用原图。",
      },
    ],
    timeline: {
      label: "一次上传示例，从开始到完成",
      previews: "预览图",
      previewsTime: "几分钟",
      live: "画廊上线",
      full: "原图",
      fullTime: "后台上传",
    },
  },
  buyers: {
    heading: "买家看到的内容",
    subtitle: "无需学习新东西。买家看到的还是熟悉的画廊，只是更早。",
    items: [
      {
        id: "protected",
        title: "照常加水印并受保护",
        body: "买家浏览的仍是带水印的预览图，如果您的画廊开启了预览保护，它也照常生效。",
      },
      {
        id: "search",
        title: "马上就能找到自己",
        body: "自拍搜索和号码布搜索都可以基于预览图进行，画廊一上线，大家就能找到自己的照片。",
      },
      {
        id: "delivery",
        title: "原图自动交付",
        body: "购买或免费下载永远不会以预览图交付。如果有人在原图到达之前购买或下载了照片，这张原图会排到队列最前面，准备好后立即交付。",
      },
    ],
  },
  strip: {
    heading: "上传时随时掌握进度",
    body: "在预览图和原图上传期间，上传工具顶部的状态条会显示画廊的进展：是否已上线、还剩多少原图，以及已购照片何时被排到最前面。画廊上线后，它还会显示过去 30 分钟内的访客人数；工作区的所有者和管理员还能看到上传开始以来的新订单。",
    exampleLabel: "示例",
    live: "已凭预览图上线",
    orders: "开始以来 3 个订单",
    visitors: "过去 30 分钟 42 位访客",
    left: "还剩 1,250 张原图",
    jumped: "2 张已下单照片的原图已排到最前面",
  },
  faq: {
    heading: "常见问题",
    items: [
      {
        q: "需要一直开着上传工具吗？",
        a: "最好开着，直到原图全部上传完成；状态条会显示还剩多少。如果提前关闭，也不会丢失任何内容。您的画廊仍凭预览图保持上线，下次在上传工具中打开这个画廊时，它会告诉您还有多少张照片在等待。再次添加同一个文件夹（之后在家里添加也可以），就只会发送缺少的原图。",
      },
      {
        q: "如果有人在原图到达前就购买了照片怎么办？",
        a: "照样可以购买。只要上传工具在运行，这张照片的原图就会排到队列最前面。订单邮件会等待文件到达，并在到达后几分钟内发出，买家绝不会误收到预览图。如果上传工具已关闭，订单会一直等到您再次添加该文件夹。上传工具会告诉您哪些照片有买家在等，这些照片会优先上传。",
      },
      {
        q: "免费下载的画廊也能用吗？",
        a: "可以。预览图一上传完，大家就能查看、搜索和下载，而且下载到的永远不会是预览图。如果有人在原图到达之前下载了照片，这张原图会排到队列最前面：下载页面会显示原图正在上传，文件一到就变成可用的下载；通过邮件发送的下载也会在那时发出。每次下载只计算一次。",
      },
      {
        q: "自拍搜索和号码布搜索能基于预览图使用吗？",
        a: "可以。人脸和号码布号码会在预览图上识别，所以预览图一上传完就能搜索。上传工具开着时，还会从您的原图中截取更清晰的特写一并发送，帮助提高搜索效果。",
      },
      {
        q: "哪些计划包含此功能？",
        a: "所有计划，包括 Lite。如果您的工作区以前用过预览图优先，这些照片会照常完成：再次添加同一个文件夹，它们的原图就会发送，相关订单和下载也会照常送达。",
      },
      {
        q: "需要额外付费吗？",
        a: "不需要。所有计划都包含此功能，每张照片只计算一次：预览图和它的原图是同一张照片。唯一的区别是，您的网络总共会多发送一点数据（约多 5%）。",
      },
      {
        q: "FTP 和 Photohawk Studio 能用吗？",
        a: "目前还不能。预览图优先是网页上传工具的功能。通过 FTP 或 Photohawk Studio 发送的照片，会像以前一样一次性上传原图。",
      },
      {
        q: "默认开启吗？",
        a: "不会。每次添加照片时，网页上传工具都会显示使用和不使用预览图优先各需多长时间，默认选中直接上传原图。想让画廊更早上线时，请选择“Previews first, originals later”；您的选择会被记住，下次沿用。选择较小的上传分辨率（“Large”或“Web”）时只上传缩小后的文件，因此不适用预览图优先。",
      },
    ],
  },
  cta: {
    heading: "下次上传时试试看",
    body: "所有计划的网页上传工具都提供预览图优先。添加照片并选择预览图优先，您的画廊几分钟就能上线。",
    button: "打开上传工具",
  },
};

export default zh;
